import { useContext } from 'react';
import { Navigate } from 'react-router-dom';
import AuthContext from '../../context/authContext';
import { Box } from '@mui/material';
import EditableList from '../EditableList/EditableList';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

function RvpAvailabilityManagement() {
  const authCtx = useContext(AuthContext);

  if (!authCtx.checkRight('resourceVoucherAvailability:read')) {
    return <Navigate to='/welcome' replace />;
  }

  const schema = {
    name: {
      label: 'Name',
      api: { itemField: 'name' },
      type: 'text',
      required: true,
      minWidth: '200px',
    },
    priority: {
      label: 'Priorität',
      api: { itemField: 'priority' },
      type: 'number',
      required: true,
      minWidth: '120px',
    },
    validFrom: {
      label: 'Gültig von',
      api: { itemField: 'validFrom' },
      type: 'date',
      required: true,
      minWidth: '160px',
    },
    validTo: {
      label: 'Gültig bis',
      api: { itemField: 'validTo' },
      type: 'date',
      required: true,
      minWidth: '160px',
    },
    specialTypes: {
      label: 'Spezialtypen',
      type: 'multi-select',
      required: false,
      minWidth: '200px',
      options: [
        { value: 'holiday', label: 'Feiertag' },
        { value: 'vacation', label: 'Ferien' },
        { value: 'summerVacation', label: 'Sommerferien' },
        { value: 'bridgeDay', label: 'Brückentag' },
      ],
    },
    allowOutsideOpeningHours: {
      label: 'Außerhalb Öffnungszeiten zulassen',
      api: { itemField: 'allowOutsideOpeningHours' },
      type: 'boolean',
      required: false,
      minWidth: '260px',
    },
    hours: {
      label: 'Zeitslots',
      type: 'nested',
      addButtonText: 'Neue Zeit hinzufügen',
      excludeFromAdd: true,
      api: { itemField: 'hours', nestedEndpoint: 'hours' },
      schema: {
        dayCode: {
          label: 'Wochentage (0=So, 1=Mo, ...)',
          type: 'array',
          placeholder: 'z. B. 1,2,3',
          transformValue: value =>
            value
              .toString()
              .split(',')
              .map(v => parseInt(String(v).trim(), 10))
              .filter(v => !Number.isNaN(v)),
          listDisplayValue: value => {
            if (!value || !Array.isArray(value) || value.length === 0) return '—';
            return value.join(', ');
          },
          required: true,
        },
        start: { label: 'Start (HH:MM)', type: 'time', required: true },
        end: { label: 'Ende (HH:MM)', type: 'time', required: true },
      },
    },
    assignedEntertainments: {
      label: 'Zugeordnete Entertainments',
      type: 'multi-select',
      api: {
        fieldName: 'entertainment',
        itemField: 'assignedEntertainments',
        idName: 'entertainmentId',
        resource: 'entertainment',
        labelField: 'longName',
        valueField: '_id',
        skipSpecial: true,
        objectify: { idProp: 'entertainmentId', labelProp: 'name' },
      },
      required: false,
      listDisplayValue: values => {
        if (!Array.isArray(values) || values.length === 0) return '—';
        return values
          .map(v => v?.name || '')
          .filter(Boolean)
          .join(', ');
      },
    },
    assignedResourceVoucherProducts: {
      label: 'Zugeordnete Gutscheinprodukte',
      type: 'multi-select',
      api: {
        fieldName: 'resourceVoucherProduct',
        itemField: 'assignedResourceVoucherProducts',
        idName: 'resourceVoucherProductId',
        resource: 'resourceVoucherProduct',
        labelField: 'name',
        valueField: '_id',
        skipSpecial: true,
        objectify: { idProp: 'resourceVoucherProductId', labelProp: 'name' },
      },
      required: false,
      listDisplayValue: values => {
        if (!Array.isArray(values) || values.length === 0) return '—';
        return values
          .map(v => v?.name || '')
          .filter(Boolean)
          .join(', ');
      },
    },
  };

  return (
    <Box sx={viewRootTextPrimarySx}>
    <EditableList
      schema={schema}
      title='Neue Gutscheinprodukt-Verfügbarkeit anlegen'
      resourceType='rvpAvailability'
      parentIsEditable={authCtx.checkRight('resourceVoucherAvailability:update')}
      options={{ titleKey: 'name' }}
    />
    </Box>
  );
}

export default RvpAvailabilityManagement;
